import { getUploadedIdentifierForRow } from './identifierDisplay';
import {
    buildCanonicalRiskComparisonRows,
    getCanonicalRiskIdentity,
} from './riskOverviewIdentityJoin';
import { isWatchlistGroupingAttribute } from './watchlistAttributePresentation';
import { isSystemCountryDefinition } from '../components/watchlist_table/watchlistAttributeDefinitions';

const ACTUAL_METRICS = [
    'quantity',
    'nmv',
    'idioVol',
    'idioRiskDollar',
    'idioVarPct',
    'factorVarPct',
    'factorVol',
    'factorRiskDollar',
    'mcfr_bp',
    'mctr_bp',
    'mcsr_bp',
];
const NULL_ACTUAL_METRICS = Object.freeze(
    Object.fromEntries(ACTUAL_METRICS.map((field) => [field, null])),
);
const ZERO_ABSENT_POSITION_CONTRIBUTIONS = Object.freeze({
    idioRiskDollar: 0,
    factorRiskDollar: 0,
});
const ZERO_ABSENT_SELECTED_CONTRIBUTIONS = Object.freeze(
    Object.fromEntries(Object.keys(ZERO_ABSENT_POSITION_CONTRIBUTIONS).map(
        (field) => [`opt_${field}`, 0],
    )),
);

function finiteNumberOrNull(value) {
    if (value === null || value === undefined || value === '') return null;
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
}

function positionIdentifier(row, optimizedRow) {
    const uploadedIdentifier = getUploadedIdentifierForRow({
        ...row,
        inbound_id_type: row?.inbound_id_type ?? optimizedRow?.inbound_id_type,
        inbound_id_value: row?.inbound_id_value ?? optimizedRow?.inbound_id_value,
    });
    if (uploadedIdentifier.value || row?._isOptimizedOnly !== true) {
        return uploadedIdentifier;
    }

    const sedol = row?.display_sedol
        ?? row?.sedol
        ?? optimizedRow?.display_sedol
        ?? optimizedRow?.sedol;
    const normalizedSedol = sedol == null ? '' : String(sedol).trim();
    return normalizedSedol
        ? { type: 'SEDOL', value: normalizedSedol }
        : uploadedIdentifier;
}

function optimizedOnlyActualRow(row) {
    return {
        ...row,
        ...NULL_ACTUAL_METRICS,
        ...ZERO_ABSENT_POSITION_CONTRIBUTIONS,
        _optimizedFields: row,
        display_sedol: row?.display_sedol ?? row?.sedol ?? null,
        sedol: row?.display_sedol ?? row?.sedol ?? null,
        ticker: row?.ticker ?? null,
        inbound_id_type: row?.inbound_id_type ?? null,
        inbound_id_value: row?.inbound_id_value ?? null,
        price: finiteNumberOrNull(row?.price),
        local_price: finiteNumberOrNull(row?.local_price),
        local_currency: row?.local_currency ?? null,
    };
}

function prefixOptimizedFields(optimizedRow) {
    return Object.fromEntries(
        Object.entries(optimizedRow ?? {}).map(([key, value]) => [`opt_${key}`, value]),
    );
}

function buildListingLabelsByIdentity(...sources) {
    const labelsByIdentity = new Map();
    sources.flatMap((rows) => (Array.isArray(rows) ? rows : [])).forEach((row) => {
        const identity = getCanonicalRiskIdentity(row);
        if (identity && row?.msi_listing_label != null && !labelsByIdentity.has(identity)) {
            labelsByIdentity.set(identity, row.msi_listing_label);
        }
    });
    return labelsByIdentity;
}

function buildWatchlistAttributeColumns(snapshot) {
    const definitions = Array.isArray(snapshot?.definitions) ? snapshot.definitions : [];
    return definitions
        .filter(isWatchlistGroupingAttribute)
        .map((definition, index) => {
            const definitionId = String(definition.attribute_definition_id);
            const displayName = String(definition.display_name || '').trim();
            return {
                definitionId,
                displayName,
                displayOrder: Number.isFinite(Number(definition.display_order))
                    ? Number(definition.display_order)
                    : index,
                field: `watchlist_attribute:${definitionId}`,
                isSystemCountry: isSystemCountryDefinition(definition),
            };
        })
        .filter(({ definitionId, displayName }) => Boolean(definitionId && displayName))
        .sort((left, right) => (
            left.displayOrder - right.displayOrder
            || left.displayName.localeCompare(right.displayName)
            || left.definitionId.localeCompare(right.definitionId)
        ))
        .map(({ definitionId, displayName, field, isSystemCountry }) => Object.freeze({
            definitionId,
            displayName,
            field,
            isSystemCountry,
        }));
}

function buildWatchlistAttributeValuesByIdentity(snapshot, attributeColumns) {
    const allowedDefinitionIds = new Set(
        attributeColumns.map(({ definitionId }) => definitionId),
    );
    const valuesByIdentity = new Map();
    (Array.isArray(snapshot?.values) ? snapshot.values : []).forEach((item) => {
        const identity = getCanonicalRiskIdentity(item);
        const definitionId = String(item?.attribute_definition_id || '');
        if (!identity || !allowedDefinitionIds.has(definitionId)) return;
        if (!valuesByIdentity.has(identity)) {
            valuesByIdentity.set(identity, new Map());
        }
        valuesByIdentity.get(identity).set(definitionId, item?.value_text ?? null);
    });
    return valuesByIdentity;
}

function projectWatchlistAttributes(identity, attributeColumns, valuesByIdentity) {
    const values = identity == null ? null : valuesByIdentity.get(identity);
    return Object.fromEntries(attributeColumns.map(({ definitionId, field }) => [
        field,
        values?.get(definitionId) ?? null,
    ]));
}

function formatFixed(value) {
    const numericValue = finiteNumberOrNull(value);
    return numericValue === null ? '' : numericValue.toFixed(2);
}

export function formatRiskOverviewPositionLocalPrice(value, currency) {
    const numericValue = finiteNumberOrNull(value);
    if (numericValue === null) return '-';

    const formatted = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
    }).format(numericValue);
    const normalizedCurrency = currency == null ? '' : String(currency).trim();
    return normalizedCurrency ? `${formatted} ${normalizedCurrency}` : formatted;
}

function formatRounded(value) {
    const numericValue = finiteNumberOrNull(value);
    return numericValue === null ? '' : Math.round(numericValue);
}

function buildPositionExportRow(row, includeSelectedExport, attributeColumns) {
    return {
        Identifier: row.display_identifier
            ? [row.display_identifier_type, row.display_identifier].filter(Boolean).join(' ')
            : '',
        Ticker: row.ticker,
        Price: formatFixed(row.price),
        'Local Price ($)': formatRiskOverviewPositionLocalPrice(
            row.local_price,
            row.local_currency,
        ),
        Quantity: row.quantity,
        ...(includeSelectedExport ? { 'Quantity OPT': formatRounded(row.opt_quantity) } : {}),
        GMV: row.nmv,
        ...(includeSelectedExport ? { 'GMV OPT': row.opt_nmv } : {}),
        ...Object.fromEntries(attributeColumns.map(({ displayName, field }) => [
            displayName,
            row[field] ?? '',
        ])),
        'Idio Risk ($)': formatRounded(row.idioRiskDollar),
        ...(includeSelectedExport ? {
            'Idio Risk OPT($)': formatRounded(row.opt_idioRiskDollar),
        } : {}),
        'Factor Risk ($)': formatRounded(row.factorRiskDollar),
        ...(includeSelectedExport ? {
            'Factor Risk OPT($)': formatRounded(row.opt_factorRiskDollar),
        } : {}),
        'MCFR (bp)': formatRounded(row.mcfr_bp),
        ...(includeSelectedExport ? { 'MCFR (bp) OPT': formatRounded(row.opt_mcfr_bp) } : {}),
        'MCTR (bp)': formatRounded(row.mctr_bp),
        ...(includeSelectedExport ? { 'MCTR (bp) OPT': formatRounded(row.opt_mctr_bp) } : {}),
        'MCSR (bp)': formatRounded(row.mcsr_bp),
        ...(includeSelectedExport ? { 'MCSR (bp) OPT': formatRounded(row.opt_mcsr_bp) } : {}),
    };
}

export function buildRiskOverviewPositionPresentation({
    holdings = [],
    optimizedHoldings = [],
    industries = [],
    watchlistAttributeSnapshot = null,
    includeSelectedExport,
    selectedHoldingsLoaded,
} = {}) {
    if (typeof includeSelectedExport !== 'boolean') {
        throw new TypeError('Position export selection must be explicitly enabled or disabled.');
    }
    if (typeof selectedHoldingsLoaded !== 'boolean') {
        throw new TypeError('Selected position display state must be explicitly available or unavailable.');
    }
    const comparison = buildCanonicalRiskComparisonRows(holdings, optimizedHoldings);
    const listingLabelsByIdentity = buildListingLabelsByIdentity(
        industries,
        holdings,
        optimizedHoldings,
    );
    const attributeColumns = buildWatchlistAttributeColumns(watchlistAttributeSnapshot);
    const watchlistAttributeValuesByIdentity = buildWatchlistAttributeValuesByIdentity(
        watchlistAttributeSnapshot,
        attributeColumns,
    );

    const rows = comparison.rows.map((comparisonRow) => {
        const row = comparisonRow._isOptimizedOnly
            ? optimizedOnlyActualRow(comparisonRow)
            : comparisonRow;
        const identity = getCanonicalRiskIdentity(row);
        const optimizedFields = row._isOptimizedOnly
            ? row._optimizedFields
            : (identity ? comparison.optimizedByIdentity.get(identity) : null);
        const identifier = positionIdentifier(row, optimizedFields);
        const localPrice = finiteNumberOrNull(row.local_price)
            ?? finiteNumberOrNull(optimizedFields?.local_price);

        return Object.freeze({
            ...row,
            id: row._riskOverviewRowKey,
            sedol: row.display_sedol ?? row.sedol ?? null,
            display_sedol: row.display_sedol ?? row.sedol ?? null,
            display_identifier: identifier.value,
            display_identifier_type: identifier.type,
            price: finiteNumberOrNull(row.price),
            local_price: localPrice,
            local_currency: row.local_currency ?? optimizedFields?.local_currency ?? null,
            ...(optimizedFields
                ? prefixOptimizedFields(optimizedFields)
                : (selectedHoldingsLoaded ? ZERO_ABSENT_SELECTED_CONTRIBUTIONS : {})),
            ...projectWatchlistAttributes(
                identity,
                attributeColumns,
                watchlistAttributeValuesByIdentity,
            ),
            quantity: comparisonRow._isOptimizedOnly ? 0 : row.quantity,
            nmv: comparisonRow._isOptimizedOnly ? 0 : row.nmv,
            opt_quantity: optimizedFields == null
                ? (selectedHoldingsLoaded ? 0 : undefined)
                : optimizedFields.quantity,
            opt_nmv: optimizedFields == null
                ? (selectedHoldingsLoaded ? 0 : undefined)
                : optimizedFields.nmv,
            msi_listing_label: identity == null
                ? null
                : listingLabelsByIdentity.get(identity) ?? null,
        });
    });
    const exportSourceRows = includeSelectedExport
        ? rows
        : rows.filter((row) => row._isOptimizedOnly !== true);
    const exportRows = exportSourceRows.map((row) => Object.freeze(
        buildPositionExportRow(row, includeSelectedExport, attributeColumns),
    ));

    return Object.freeze({
        rows: Object.freeze(rows),
        exportRows: Object.freeze(exportRows),
        attributeColumns: Object.freeze(attributeColumns),
        diagnostics: Object.freeze({ ...comparison.diagnostics }),
    });
}
